import qs from "query-string";
import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";

import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";

type PoliciesBuilder = EndpointBuilder<BaseQueryFn, "Policies", "policiesApi">;

type GetPoliciesParams = {
    workspaceId: number;
};

export const policiesPageSize = 200;

function getPolicies(builder: PoliciesBuilder) {
    return builder.query<PaginatedResponse<API.PolicyViewModel>, GetPoliciesParams>({
        providesTags: ["Policies"],

        queryFn: async ({ workspaceId }, _api, _extraOptions, baseQuery) => {
            const base = await RouteService.getPoliciesRoute();
            let list: API.PolicyViewModel[] = [];
            let pageIndex = pageIndexOffset;
            let page: PaginatedResponse<API.PolicyViewModel>;

            do {
                const params = { workspaceId, pageIndex, pageSize: policiesPageSize };
                // eslint-disable-next-line no-await-in-loop -- sequential pagination: each page depends on the previous count
                const result = await baseQuery({ url: base, params, paramsSerializer: () => qs.stringify(params) });
                if (result.error) {
                    return {
                        error: transformErrorResponse(result.error, i18next.t("common:error.policies.failedToGetPolicies")),
                    };
                }
                page = result.data as PaginatedResponse<API.PolicyViewModel>;
                list = list.concat(page.list);
                pageIndex += 1;
            } while (page.list.length === policiesPageSize && list.length < page.count);

            return { data: { ...page, list } };
        },
    });
}

type PolicyIdParam = {
    id: string;
};

function getPolicyById(builder: PoliciesBuilder) {
    return builder.query<ApiResponse<API.PolicyViewModel>, PolicyIdParam>({
        providesTags: ["Policies"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToGetPolicy")),
        query: async ({ id }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}`,
            };
        },
    });
}

export type CreatePolicyPayload = {
    workspaceId: number;
    name: string;
    content: API.Policy[];
};

function createPolicy(builder: PoliciesBuilder) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, CreatePolicyPayload>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToCreatePolicy")),
        query: async ({ content, ...payload }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: base,
                method: "POST",
                data: { ...payload, content },
            };
        },
    });
}

export type UpdatePolicyPayload = PolicyIdParam & {
    name: string;
    content: API.Policy[];
};

function updatePolicy(builder: PoliciesBuilder) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, UpdatePolicyPayload>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToUpdatePolicy")),
        query: async ({ id, content, ...payload }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}`,
                method: "PUT",
                data: { ...payload, content },
            };
        },
    });
}

function deletePolicy(builder: PoliciesBuilder) {
    return builder.mutation<BasicApiResponse, PolicyIdParam>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToDeletePolicy")),
        query: async ({ id }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}`,
                method: "DELETE",
            };
        },
    });
}

export type PolicyGroupsPayload = PolicyIdParam & {
    groupIds: number[];
};

function assignPolicyToGroups(builder: PoliciesBuilder) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, PolicyGroupsPayload>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToAssignPolicy")),
        query: async ({ id, groupIds }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}/groups`,
                method: "POST",
                data: { groupIds },
            };
        },
    });
}

function unassignPolicyFromGroups(builder: PoliciesBuilder) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, PolicyGroupsPayload>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToUnassignPolicy")),
        query: async ({ id, groupIds }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}/groups`,
                method: "DELETE",
                data: { groupIds },
            };
        },
    });
}

export default {
    getPolicies,
    getPolicyById,
    createPolicy,
    updatePolicy,
    deletePolicy,
    assignPolicyToGroups,
    unassignPolicyFromGroups,
};
